/** One response-bound reader of an already completed Accounting answer.
 * Financial execution and its receipts never depend on audio delivery.
 */
const IDENTITY_FIELDS = ['playback_id','turn_id','answer_sha256','session_id','spoken_sha256','speech_format'];
const SPEECH_FORMATS = ['plain_v1','markdown_v1','raw_fallback_v1'];

export class RealtimeNarration {
  constructor({ sessionId, transport, onState = () => {}, report = () => {}, timeoutMs = 30000 }) {
    Object.assign(this, { sessionId, transport, onState, report, timeoutMs });
    this.known = new Map(); this.inflight = null; this.next = null;
    this.closed = false; this.failed = false;
  }

  observe(action) {
    try { Promise.resolve(action()).catch(() => {}); } catch (_) { /* Optional observation. */ }
  }

  state(phase, entry, detail = null) {
    if (!this.closed) this.observe(() => this.onState({ phase, playbackId: entry?.package.playback_id, detail }));
  }

  record(entry, kind, extra = {}) {
    this.observe(() => this.report(entry.package.playback_id, kind, {
      provider_response_id: entry.responseId || null, ...extra,
    }));
  }

  request(value) {
    if (this.closed || this.failed) return false;
    const response = value.response;
    const metadata = response?.metadata;
    if (value.session_id !== this.sessionId || response?.conversation !== 'none' || !Array.isArray(response?.tools) || response.tools.length !== 0
        || typeof value.text !== 'string' || !value.text.trim()
        || typeof value.spoken_text !== 'string' || !value.spoken_text.trim()
        || !SPEECH_FORMATS.includes(value.speech_format)
        || (value.speech_format !== 'markdown_v1' && value.spoken_text !== value.text)
        || response?.output_modalities?.length !== 1 || response.output_modalities[0] !== 'audio'
        || IDENTITY_FIELDS.some((key) => typeof value[key] !== 'string' || !value[key] || metadata?.[key] !== value[key])
        || response.input?.length !== 1 || response.input[0].role !== 'user'
        || response.input[0].content?.length !== 1 || response.input[0].content[0].type !== 'input_text'
        || response.input[0].content[0].text !== JSON.stringify({ response_text:value.spoken_text,require_repeat_verbatim:true })) throw new Error('Narration must match its owned completed answer.');
    const previous = this.known.get(value.playback_id)?.package
      || (this.next?.playback_id === value.playback_id ? this.next : null);
    if (previous) {
      if ([...IDENTITY_FIELDS, 'text', 'spoken_text'].some((key) => previous[key] !== value[key])) {
        throw new Error('Narration must match its owned completed answer.');
      }
      return false;
    }
    this.next = JSON.parse(JSON.stringify(value));
    if (this.inflight) this.interrupt(false);
    else this.launch();
    return true;
  }

  launch() {
    if (this.closed || this.failed || this.inflight || !this.next) return;
    const value = this.next; this.next = null;
    const entry = { package: value, responseId: null, cancelled: false, done: null,
      bufferStarted: false, bufferStopped: false, bufferCleared: false, clearRequested: false,
      speakerFinished: false, transcript: '' };
    this.known.set(value.playback_id, entry); this.inflight = entry;
    this.state('audio_starting', entry);
    if (!this.transport.sendOutput({ type: 'response.create', event_id: value.playback_id, response: value.response }, this.sessionId)) {
      this.fail(entry, 'audio_request_unconfirmed'); return;
    }
    this.record(entry, 'requested'); this.watch(entry);
  }

  watch(entry) {
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => { if (this.inflight === entry) this.fail(entry, 'audio_stalled'); }, this.timeoutMs);
  }

  cancel(entry) {
    if (entry.responseId && !entry.done && !entry.cancelRequested) {
      entry.cancelRequested = true;
      this.transport.sendOutput({ type: 'response.cancel', event_id: entry.package.playback_id+'.cancel',
        response_id: entry.responseId }, this.sessionId);
    }
    if (entry.bufferStarted && !entry.clearRequested && (this.inflight === entry || this.failed)) {
      entry.clearRequested = true;
      this.transport.sendOutput({ type: 'output_audio_buffer.clear', event_id: entry.package.playback_id+'.clear' }, this.sessionId);
    }
  }

  interrupt(dropQueued = true) {
    if (dropQueued) this.next = null;
    this.transport.stopOutput(this.sessionId); // Immediate local mute, no financial compensation.
    const entry = this.inflight;
    if (!entry) return;
    if (!entry.cancelled) { entry.cancelled = true; this.record(entry, 'interrupted'); this.state('interrupted', entry); }
    this.cancel(entry); this.retire(entry);
  }

  fail(entry, detail) {
    if (this.inflight !== entry) return;
    this.transport.stopOutput(this.sessionId); entry.cancelled = true;
    this.cancel(entry); clearTimeout(entry.timer);
    this.failed = true; this.next = null; this.inflight = null;
    this.record(entry, 'failed'); this.state('audio_unavailable', entry, detail);
  }

  retire(entry) {
    if (this.inflight !== entry || !entry.done) return;
    const cancelled = entry.cancelled && (!entry.bufferStarted || entry.bufferCleared);
    const finished = !entry.cancelled && entry.done === 'completed' && entry.bufferStopped
      && (entry.speakerFinished || entry.speakerDrained);
    if (!cancelled && !finished) return;
    clearTimeout(entry.timer); this.inflight = null;
    if (finished) {
      this.record(entry, entry.speakerFinished ? 'finished' : 'drained', { observation_basis: entry.finishBasis || null });
      this.state('finished', entry, entry.speakerFinished ? null : 'audio_signal_unobserved');
    }
    this.launch(); // Only the latest replacement; never a growing speech queue.
  }

  event(event, sessionId) {
    if (sessionId !== this.sessionId) return false;
    if (this.closed) return true;
    if (event.type === 'response.created') {
      const response = event.response;
      const entry = this.known.get(response?.metadata?.playback_id);
      if (!entry || response.conversation_id !== null
          || IDENTITY_FIELDS.some((key) => response.metadata[key] !== entry.package[key])
          || (entry.responseId && entry.responseId !== response.id)) return false;
      entry.responseId = response.id;
      if (entry.cancelled || this.closed || this.failed) this.cancel(entry);
      return true;
    }
    if (event.type === 'error') {
      const id = event.error?.event_id;
      const entry = [...this.known.values()].find((row) => [row.package.playback_id,
        row.package.playback_id+'.cancel',row.package.playback_id+'.clear'].includes(id));
      if (!entry) return false;
      if (id === entry.package.playback_id) this.fail(entry, 'audio_generation_failed');
      return true;
    }
    const responseId = event.response_id || event.response?.id;
    const entry = [...this.known.values()].find((row) => row.responseId === responseId);
    if (!entry) return false;
    if (entry === this.inflight) this.watch(entry);
    if (event.type === 'response.output_item.added' && event.item?.type !== 'message') throw new Error('Unexpected narration tool item');
    if (event.type === 'output_audio_buffer.started') {
      if (entry.bufferStarted) return true;
      entry.bufferStarted = true;
      if (entry !== this.inflight && this.inflight) this.fail(this.inflight, 'stale_audio_after_replacement');
      if (entry.cancelled || entry !== this.inflight) this.cancel(entry);
      else this.observe(async () => {
        const started = await this.transport.speakOutput(entry.package.playback_id, this.sessionId);
        if (!started && this.inflight === entry && !entry.cancelled) this.fail(entry, 'audio_output_unavailable');
      });
    }
    if (event.type === 'response.output_audio_transcript.done') entry.transcript = event.transcript || '';
    if (event.type === 'response.done' && !entry.done) {
      entry.done = event.response.status;
      if ((event.response.output || []).some((item) => item.type !== 'message')) throw new Error('Unexpected narration tool result');
      this.record(entry, 'generated', { generation_status: entry.done, generated_transcript: entry.transcript,
        usage: this.usage(event.response.usage) });
      if (entry.done !== 'completed' && !entry.cancelled) this.fail(entry, 'audio_generation_incomplete');
    }
    if (event.type === 'output_audio_buffer.stopped') {
      entry.bufferStopped = true;
      if (entry === this.inflight && !entry.cancelled) this.transport.drainOutput(entry.package.playback_id, this.sessionId);
    }
    if (event.type === 'output_audio_buffer.cleared') entry.bufferCleared = true;
    this.retire(entry);
    return true;
  }

  usage(value) {
    if (!value) return null;
    const fields = { total_tokens: value.total_tokens, input_tokens: value.input_tokens, output_tokens: value.output_tokens,
      audio_input_tokens: value.input_token_details?.audio_tokens, audio_output_tokens: value.output_token_details?.audio_tokens,
      text_input_tokens: value.input_token_details?.text_tokens, text_output_tokens: value.output_token_details?.text_tokens,
      cached_tokens: value.input_token_details?.cached_tokens };
    return Object.fromEntries(Object.entries(fields).filter(([, number]) => Number.isInteger(number) && number >= 0));
  }

  audioState(event) {
    const entry = this.inflight;
    if (this.closed || !entry || entry.cancelled || event.sessionId !== this.sessionId || event.playbackId !== entry.package.playback_id) return;
    this.watch(entry);
    if (event.kind === 'started' && !entry.speakerStarted) {
      entry.speakerStarted = true; this.record(entry, 'started', { observation_basis: event.basis || null }); this.state('speaking', entry);
    }
    if (event.kind === 'finished') { entry.speakerFinished = true; entry.finishBasis = event.basis || null; this.retire(entry); }
    if (event.kind === 'drained' && entry.bufferStopped && event.basis === 'provider_buffer_drained') {
      entry.speakerDrained = true; entry.finishBasis = event.basis; this.retire(entry);
    }
    if (event.kind === 'unavailable') this.fail(entry, 'audio_output_unavailable');
  }

  close() {
    if (this.closed) return;
    this.interrupt(); this.closed = true;
    for (const entry of this.known.values()) clearTimeout(entry.timer);
  }
}
